import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { TranslatedBaseError } from "../../pages/TranslatedBaseError";
import { RecoverOrphanConfig } from "../../../models/recover-orphan-config";
import { Store } from "../../../services/store.service";

@Component({
  selector: "dry-run",
  templateUrl: "./dry-run.component.html",
  styleUrls: ["./dry-run.component.scss"],
  providers: [ServiceControllerService],
})
export class DryRunComponent extends TranslatedBaseError implements OnInit {
  @Input() public serviceId: string;
  @Output() public resultsUpdated = new EventEmitter<RecoverOrphanConfig[]>();

  public isLoading: boolean = false;
  public drawerOpen: boolean = false;
  public debugMode: boolean = false;
  public results: RecoverOrphanConfig[] = [];
  public groupedResults: Map<string, Map<string, RecoverOrphanConfig[]>> = new Map();
  public sortedGroupKeys: string[] = [];
  public groupOpenState: Map<string, boolean> = new Map();
  public groupKeyMapping: Map<string, string> = new Map();
  public groupErrorCounts: Map<string, number> = new Map();

  constructor(
    private serviceControllerService: ServiceControllerService,
    public toastr: ToastrService,
    public translateService: TranslateService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super(toastr, translateService);
  }

  public ngOnInit(): void {
    // Initialize debug mode from localStorage via Store
    this.debugMode = this.store.state.common.debugMode;
    this.store.state.common.dryRunDebugMode = this.debugMode;
    this.store.debugMode$.next(this.debugMode);

    // Check if debug=true in query params, otherwise restore drawer state from store
    this.route.queryParams.subscribe((params) => {
      setTimeout(() => {
        if (params.debug === 'true') {
          this.drawerOpen = true;
          this.store.state.common.dryRunDrawerOpen = true;
          this.onDebugModeChange(true);
        } else {
          // Restore drawer state from store when component initializes
          this.drawerOpen = this.store.state.common.dryRunDrawerOpen;
        }
        if (this.drawerOpen) {
          this.openDebug(true, false);
        } else if (this.debugMode) {
          this.openDebug(false, false);
        }
      }, 2000);
    });
  }

  public openDebug(autoOpen: boolean = true, forceRefresh: boolean = false): void {
    this.hideLines();
    if (!this.isLoading) {
      if (!forceRefresh) {
        const cachedResults = this.store.state.common.dryRunResults.get(this.serviceId);
        if (cachedResults) {
          this.processResults(cachedResults, autoOpen);
          return;
        }
      }

      this.isLoading = true;
      this.serviceControllerService.recoverOrphanConfig(this.serviceId).subscribe(
        (data) => {
          this.store.state.common.dryRunResults.set(this.serviceId, data);
          this.processResults(data, autoOpen);
        },
        (error) => this.showErrorMessage(error),
        () => (this.isLoading = false)
      );
    }
  }

  private processResults(data: RecoverOrphanConfig[], autoOpen: boolean): void {
    this.results = data;
    this.groupedResults = this.groupResultsByTypeAndItem(data);
    const result = this.getSortedGroupKeys(Array.from(this.groupedResults.keys()));
    this.sortedGroupKeys = result.displayKeys;
    this.groupKeyMapping = result.mapping;
    this.initializeGroupOpenState();
    if (data.length > 0 && autoOpen) {
      this.drawerOpen = true;
      this.store.state.common.dryRunDrawerOpen = true;
      this.onDebugModeChange(true);
    }
    this.resultsUpdated.emit(data);
    if (this.isLoading) { // Only show success message if it was a fresh fetch
      this.responseSuccessful("Service orphan configuration recovered");
    }
    this.isLoading = false;
  }

  private initializeGroupOpenState(): void {
    this.groupOpenState.clear();
    this.groupErrorCounts.clear();
    this.sortedGroupKeys.forEach((groupKey) => {
      this.groupOpenState.set(groupKey, false);
      // Count total errors in this group
      const subGroups = this.groupedResults.get(groupKey);
      let errorCount = 0;
      if (subGroups) {
        subGroups.forEach((errors) => {
          errorCount += errors.length;
        });
      }
      this.groupErrorCounts.set(groupKey, errorCount);
    });
  }

  private groupResultsByTypeAndItem(results: RecoverOrphanConfig[]): Map<string, Map<string, RecoverOrphanConfig[]>> {
    const grouped = new Map<string, Map<string, RecoverOrphanConfig[]>>();

    results.forEach((item) => {
      const groupKey = this.getGroupKey(item);
      const subGroupKey = this.getSubGroupKey(item);

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, new Map());
      }

      const subGroup = grouped.get(groupKey);
      if (!subGroup.has(subGroupKey)) {
        subGroup.set(subGroupKey, []);
      }
      subGroup.get(subGroupKey).push(item);
    });

    return grouped;
  }

  private getGroupKey(item: RecoverOrphanConfig): string {
    // Group by parent type only (Registration, Role, Form, Bot, Certificate)
    if (
      item.objectType.startsWith("registration") ||
      item.objectType === "missing_determinants_in_cost" ||
      item.objectType === "missing_determinants_in_cost_formula" ||
      item.objectType === "missing_determinants_in_registration_result" ||
      item.objectType === "missing_determinants_in_registration_mandatory" ||
      item.objectType === "missing_determinants_in_registration_requirement" ||
      item.objectType === "missing_determinants_in_registration_optional") {
      return "Registrations";
    }

    // Group by Role
    if (item.objectType.startsWith("role_") || item.objectType.includes("role") || item.parentType === "USERROLE") {
      return "Roles";
    }

    // Group by Bot
    if (item.objectType.includes("mapping") || item.parentType === "BOT") {
      return "Actions";
    }

    // Group by Form type
    if (item.parentType === "GUIDEFORM" || item.parentType === "FORM" || item.parentType === "DOCUMENT" || item.parentType === "PAYMENT" || item.parentType === "SENDPAGE" || item.objectType.includes("parta_form")) {
      return "Forms";
    }

    // Group by Certificate and message templates
    if (item.parentType === "CERTIFICATE" || item.objectType === "message_moustache") {
      return "Templates";
    }

    // Group by Certificate and message templates
    if (item.objectType === "translation_moustache") {
      return "Translations";
    }

    // Group by Certificate and message templates
    if (item.objectType.toLocaleLowerCase() === "determinant") {
      return "Determinants";
    }

    // Default grouping
    return "Other";
  }

  private getSubGroupKey(item: RecoverOrphanConfig): string {
    // Sub-group by the specific item (parentName, objectId, or parentId)
    return item.objectType?.includes("role") || item.objectType?.includes("message_") ? item.componentKey : (item.parentName || item.objectId || item.parentId || "Unknown");
  }

  private getSortedGroupKeys(keys: string[]): { displayKeys: string[]; mapping: Map<string, string> } {
    const groupOrder = ["Registrations", "Forms", "Roles", "Actions", "Templates", "Determinants", "Translations", "Other"];
    const mapping = new Map<string, string>();

    const sorted = keys.sort((a, b) => {
      const aIndex = groupOrder.indexOf(a);
      const bIndex = groupOrder.indexOf(b);

      const aOrder = aIndex !== -1 ? aIndex : groupOrder.length;
      const bOrder = bIndex !== -1 ? bIndex : groupOrder.length;

      return aOrder - bOrder;
    });

    // Create mapping for consistency
    const displayKeys = sorted.map(key => {
      mapping.set(key, key);
      return key;
    });

    return { displayKeys, mapping };
  }

  public closeDrawer(): void {
    this.drawerOpen = false;
    this.store.state.common.dryRunDrawerOpen = false;
    this.showLines();
  }

  public prepareLinkToComponent(mistake: RecoverOrphanConfig): string {
    let link = "/services/" + mistake.serviceId + "/";

    if (mistake.objectType === "determinant") {
      return link + "determinantstable#" + mistake.parentId;
    }
    switch (mistake.objectType) {
      case "determinant":
        return link + "determinantstable#" + mistake.parentId;
      case "missing_determinants_in_registration_mandatory":
      case "missing_determinants_in_registration_optional":
        return link + "registrations/" + mistake.objectId + "?debug=true&bugsource=" + mistake.objectId + "&bugtype=" + mistake.objectType;
      case "missing_determinants_in_registration_result":
      case "missing_determinants_in_registration_requirement":
      case "missing_determinants_in_cost":
      case "missing_determinants_in_cost_formula":
      case "registration_result_linked_document_reference_broken":
      case "registration_requirement_linked_document_reference_broken":
        return link + "registrations/" + mistake.parentId + "?debug=true&bugsource=" + mistake.objectId + "&bugtype=" + mistake.objectType;
      case "missing_determinants_in_role":
      case "missing_determinants_in_role_institution":
      case "role_institution_missing":
      case "role_registration_missing":
        return link + "roles/" + mistake.objectId;
      case "missing_determinants_in_mapping":
      case "missing_determinants_in_mapping_filter":
        return link + "bots/" + mistake.parentId;
    }
    switch (mistake.parentType) {
      case "GUIDEFORM":
        link += "forms/guide-form";
        break;
      case "FORM":
        link += "forms/applicant-form";
        break;
      case "DOCUMENT":
        link += "forms/document-form";
        break;
      case "PAYMENT":
        link += "forms/payment";
        break;
      case "SENDPAGE":
        link += "forms/sendfile";
        break;
      case "USERROLE":
        link += "roles/" + mistake.parentId;
        break;
      case "CERTIFICATE":
        link += "print-documents/" + mistake.parentId;
        break;
    }
    return link + "%23" + mistake.componentKey + "?debug=true";
  }

  public formatTargetItem(mistake: RecoverOrphanConfig): string {
    if (!mistake.conflictingValue) {
      return mistake.componentKey;
    }
    const conflictedValue = Number(mistake.conflictingValue.replace("-",""));
    if (!conflictedValue) {
      return mistake.componentKey;
    }
    return conflictedValue.toLocaleString();
  }

  public onDebugModeChange(value: boolean): void {
    this.debugMode = value;
    this.store.state.common.debugMode = this.debugMode;
    this.store.state.common.dryRunDebugMode = this.debugMode;
    this.store.debugMode$.next(this.debugMode);

    if (!this.debugMode) {
      this.closeDrawer();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { debug: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { debug: 'true' },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  public showLines() {
    document.querySelectorAll(".flow-line").forEach((l) => {
      l.classList.remove("overlap");
    });
  }

  public hideLines() {
    document.querySelectorAll(".flow-line").forEach((l) => {
      l.classList.add("overlap");
    });
  }

  public isDebugAvailable(): boolean {
    const userLevel = this.store?.state?.common?.userLevels?.pages?.SideNavLevel;
    return userLevel === 2 || userLevel === 3;
  }
}
